// src/components/oficina/OperatorSelectModal.js
import React, { useState, useEffect } from "react";
import { User, ShieldAlert, ArrowLeft, Delete, X } from "lucide-react";
import { useOperator } from "../../contexts/OperatorContext";
import { pb } from "../../services/pocketbase";

export default function OperatorSelectModal() {
  const { 
    showSelectModal, 
    activeOperator, 
    loginOperator, 
    setShowSelectModal,
    terminalType
  } = useOperator();

  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null); // Para digitar o PIN
  const [pin, setPin] = useState("");
  const [errorShake, setErrorShake] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Carrega operadores ativos do PocketBase
  useEffect(() => {
    if (!showSelectModal) return;

    const loadOperators = async () => {
      setLoading(true);
      try {
        const records = await pb.collection("oficina_operadores").getFullList({
          filter: `ativo = true && tipo = "${terminalType}"`,
          sort: "nome",
        });
        setOperators(records);
      } catch (e) {
        console.error(`Erro ao carregar operadores para ${terminalType}:`, e);
      } finally {
        setLoading(false);
      }
    };

    loadOperators();
    setSelectedOperator(null);
    setPin("");
    setErrorMessage("");
  }, [showSelectModal]);

  // Se o PIN chegar a 4 caracteres, valida
  useEffect(() => {
    if (pin.length === 4 && selectedOperator) {
      handleVerifyPin();
    }
  }, [pin, selectedOperator]);

  if (!showSelectModal) return null;

  const handleVerifyPin = async () => {
    setLoading(true);
    setErrorMessage("");
    const success = await loginOperator(selectedOperator, pin);
    setLoading(false);

    if (success) {
      // Login com sucesso, limpa estados locais
      setSelectedOperator(null);
      setPin("");
    } else {
      // Erro, executa animação de shake e limpa o pin
      setErrorShake(true);
      setErrorMessage("PIN INCORRETO");
      setPin("");
      setTimeout(() => setErrorShake(false), 500);
    }
  };

  const handleKeypadPress = (num) => {
    if (pin.length < 4) {
      setPin(prev => prev + num);
      setErrorMessage("");
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPin("");
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-6px); }
          20%, 40%, 60%, 80% { transform: translateX(6px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
        
        {/* HEADER */}
        <div className="bg-slate-900 p-6 text-white shrink-0 relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-500 p-2 rounded-xl text-white">
              <ShieldAlert size={20} />
            </div>
            <div>
              <h3 className="font-bold text-sm">Identificação de Operador</h3>
              <p className="text-[10px] text-slate-400 mt-0.5">Terminal Compartilhado Oficina</p>
            </div>
          </div>
          {/* Só permite fechar se já houver um operador ativo */}
          {activeOperator && (
            <button 
              onClick={() => setShowSelectModal(false)}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0 bg-slate-50/50">
          
          {loading && operators.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400 text-xs font-semibold">
              <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin"></div>
              <span>Carregando operadores...</span>
            </div>
          ) : !selectedOperator ? (
            /* ================= VIEW 1: GRADE DE OPERADORES ================= */
            <div className="space-y-4">
              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">
                Quem está operando agora?
              </span>

              {operators.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-xs">
                  Nenhum operador ativo cadastrado.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {operators.map((op) => (
                    <button
                      key={op.id}
                      onClick={() => setSelectedOperator(op)}
                      className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-slate-800 hover:shadow-md transition-all active:scale-[0.98]"
                    >
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm"
                        style={{ backgroundColor: op.cor || "#64748b" }}
                      >
                        {op.nome?.substring(0, 2).toUpperCase()}
                      </div>
                      <span className="font-bold text-slate-800 text-xs tracking-wide truncate max-w-full">
                        {op.nome}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* ================= VIEW 2: TECLADO DE PIN ================= */
            <div className="flex flex-col items-center gap-6">
              {/* Voltar */}
              <button
                onClick={() => {
                  setSelectedOperator(null);
                  setPin("");
                  setErrorMessage("");
                }}
                className="self-start flex items-center gap-1.5 text-[10px] font-bold text-slate-500 hover:text-slate-800"
              >
                <ArrowLeft size={12} /> VOLTAR OPERADORES
              </button>

              {/* Avatar do Operador Selecionado */}
              <div className="flex flex-col items-center gap-2">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-md"
                  style={{ backgroundColor: selectedOperator.cor || "#64748b" }}
                >
                  {selectedOperator.nome?.substring(0, 2).toUpperCase()}
                </div>
                <span className="font-bold text-slate-800 text-sm">PIN de {selectedOperator.nome}</span>
              </div>

              {/* Bolinhas de Dígitos do PIN */}
              <div className={`flex gap-4 my-2 ${errorShake ? "animate-shake" : ""}`}>
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={`w-4 h-4 rounded-full border-2 transition-all ${
                      pin.length > index
                        ? "bg-slate-900 border-slate-900 scale-110 shadow-sm"
                        : "border-slate-300 bg-transparent"
                    }`}
                  />
                ))}
              </div>

              {errorMessage && (
                <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-3 py-1 rounded-full animate-bounce">
                  {errorMessage}
                </span>
              )}

              {/* Teclado Virtual Numérico */}
              <div className="grid grid-cols-3 gap-3 w-full max-w-[280px]">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <button
                    key={num}
                    onClick={() => handleKeypadPress(num)}
                    className="h-14 rounded-2xl bg-white border border-slate-200 hover:bg-slate-50 active:bg-slate-100 font-bold text-lg text-slate-800 shadow-sm active:scale-95 transition-all flex items-center justify-center"
                  >
                    {num}
                  </button>
                ))}
                
                {/* Botão C (Limpar) */}
                <button
                  onClick={handleClear}
                  className="h-14 rounded-2xl bg-slate-100 font-bold text-xs text-slate-600 hover:bg-slate-200 active:scale-95 transition-all flex items-center justify-center"
                >
                  LIMPAR
                </button>

                {/* Botão 0 */}
                <button
                  onClick={() => handleKeypadPress(0)}
                  className="h-14 rounded-2xl bg-white border border-slate-200 hover:bg-slate-50 active:bg-slate-100 font-bold text-lg text-slate-800 shadow-sm active:scale-95 transition-all flex items-center justify-center"
                >
                  0
                </button>

                {/* Botão Backspace */}
                <button
                  onClick={handleBackspace}
                  className="h-14 rounded-2xl bg-slate-100 text-slate-600 hover:bg-slate-200 active:scale-95 transition-all flex items-center justify-center"
                >
                  <Delete size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
